import { Component, ViewChild, ElementRef } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import {
  AuthService, BreadcrumbService, CompanyService, ConfirmDialogService, HighlightService, HomeService, StateService
} from 'src/app/services';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  ProductName, 
  SituViewWebApiClient, 
  RenderedOutputStatus,
  IfcFileModel,
  TempIfcFileError,
  TempIfcFile,
  GetStyleTemplateThemesRequest,
  GetStyleTemplateThemesResponse
} from 'situview-webapi-client';
import {
  SituRenderServiceWebApiClient, FileParameter,
  CreateSituConnectResponse,
  GetSituConnectRequest,
  GetSituConnectResponse,
  HomeStatus,
  CopySituConnectRequest,
  CopySituConnectResponse
} from 'siturenderservice-webapi-client';
import { ConfigService } from 'src/app/services/config.service';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import 'prismjs';
import 'prismjs/components/prism-markup';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-regex';
import { ActivatedRoute, Router } from '@angular/router';
declare var Prism: any;
import * as FileSaver from 'file-saver';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { Guid } from 'guid-typescript';
import { ConfirmDialogData } from 'src/app/components/confirm-dialog/ConfirmDialogData';
import { Role } from 'src/app/shared/models/role.model';
import { ReleaseStyleTemplateDialogComponent, ReleaseStyleTemplateDialogComponentResultData } from '../../admin/product-releases/release-style-template-dialog/release-style-template-dialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ReleaseIssueDialogComponent, ReleaseIssueDialogComponentResultData } from '../../admin/product-releases/release-issue-dialog/release-issue-dialog.component';
import { BaseIngredient, ListRecipeEventSummariesRequest, ListRecipeEventSummariesResponse, ListSituConnectSummariesRequest, ListSituConnectSummariesResponse, RecipeEventSummary, RecipeStatus, SituAnalyticsWebApiClient, SituConnectSummary } from 'situanalytics-webapi-client';
import { SituConnectLoadItem } from './situ-connect-config-list-card/situ-connect-config-list-card.component';

@Component({
  selector: 'situ-connect',
  templateUrl: './situ-connect.component.html',
  styleUrls: ['./situ-connect.component.scss']
})
export class SituConnectComponent extends SituBaseComponent {
  /* view high level controls */
  collapsed: boolean = false;
  paramId: string | undefined;
  paramToId: string | undefined;
  initialised: boolean = false;
  isReleasePermitted: boolean = false;
  readonly connectProductName: ProductName = ProductName.Connect;
  productNames = ProductName;
  renderType: string | undefined;

  /* view data */
  readonly renderedOutputStatusEnum = RenderedOutputStatus;
  refreshToken: string | undefined;
  saveTemplateTrigger: string | undefined;
  styleTemplateSavePending: boolean = false;
  @ViewChild('homeConfiguration') homeConfiguration: ElementRef | undefined;
  @ViewChild('styleSelections') styleSelections: ElementRef | undefined;
  @ViewChild('configurationOptions') configurationOptions: ElementRef | undefined;
  
  /* form set up */
  facadeSettingsForm!: FormGroup;
  get facadeConfigControl(): AbstractControl { return this.facadeSettingsForm?.controls?.facadeConfigName; }

  /* embed code */
  apiCode = '';
  prismApiCode = '';
  embedCode = '';
  prismEmbedCode = '';
  highlighted: boolean = false;
  embedFacade: boolean = true;

  private _loading = false;
  get loading(): boolean { return this._loading; }
  set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  static readonlyType: string = "connect";

  constructor(
    authService: AuthService,
    stateService: StateService,
    private clipboard: Clipboard,
    private snackBar: MatSnackBar,
    private breadcrumbService: BreadcrumbService,
    private configService: ConfigService,
    private formBuilder: FormBuilder,
    private companyService: CompanyService,
    private highlightService: HighlightService,
    private route: ActivatedRoute,
    private router: Router,
    private renderServiceClient: SituRenderServiceWebApiClient,
    private situViewClient: SituViewWebApiClient,
    private dialogService: ConfirmDialogService,
    private homeService: HomeService,
    private dialog: MatDialog,
    private situAnalyticsClient: SituAnalyticsWebApiClient) { 
      super(authService, stateService);
      if (this.route.snapshot.params['id']) {
        this.paramId = this.route.snapshot.params['id'];
      }
      this.firstFormGroup = this.formBuilder.group({
        customerName: ['', Validators.required],
        customerReference: ['', Validators.required]
      });
  }
  firstFormGroup: FormGroup | undefined;

  ngOnInit(): void {
    super.ngOnInit();
    this.loadRecipeEventSummaries()
  }

  protected onSelectedCompanyChange(): void {
    this.loadRecipeEventSummaries();
  }

  styles: string[] | undefined;
  styleTemplateId: string | undefined;
  onStyleTemplateIdUpdates(styleTemplateId: string) {
    let request = new GetStyleTemplateThemesRequest();
    request.companyId = this.selectedCompany?.id;
    request.styleTemplateId = styleTemplateId;

    this.situViewClient.getStyleTemplateThemes(request)
      .then((response: GetStyleTemplateThemesResponse) => {
        if (response.success && response.themes) {
          this.styles = response.themes;
          this.styleTemplateId = styleTemplateId;
        }
      })
      .catch((err: any) => {

      });
  }

  toggleCollapse() {
    this.collapsed = !this.collapsed;
  }

  tempIfcFileErrors: TempIfcFileError[] = [];
  tempIfcFiles: TempIfcFile[] = [];
  uploadInProgress: boolean = false;
  files: FileParameter[] = [];
  ifcFile: IfcFileModel = new IfcFileModel({ fileName: "", ifcType: undefined });
  onFileChosen(event: any) {
    const file: File = event.target.files[0];

    var fileExt = file?.name?.split('.').pop();
    if (fileExt !== 'ifc') {
      this.showMessage("Only IFC files are allowed");
      event.target.value = '';
      return;
    }

    this.ifcFile.fileName = file.name;
    this.files.push({ data: file, fileName: file.name });
  }

  onRemoveIFCFile() {
    this.ifcFile = new IfcFileModel({ fileName: "", ifcType: undefined });
    this.files = [];
  }

  onUploadClick() {
    let buttonId = "IFCFile_";
    document.getElementById(buttonId)?.click();
  };

  situConnectId: string | undefined;
  render() {
    if (!this.styles || !this.selectedCompany || !this.files || !this.renderType) {
      this.showMessage("Sorry! Please ensure every section is filled out correctly");
      return;
    }
    this.loading = true;
    this.situConnectId = undefined;
    this.stopPollingRenderStatus();

    this.renderServiceClient.createSituConnect(
        undefined, //daterequestedFor
        this.selectedCompany?.id,
        this.renderType,
        undefined, //situconnectid
        JSON.stringify(this.createConnectConfig(this.firstFormGroup?.get('customerName')?.value, this.firstFormGroup?.get('customerReference')?.value, this.styles)),
        false, 
        "Production", //environment
        this.notifyMe ? "Email" : "None", //notifyCompleteChannel
        this.files
      )
      .then((response: CreateSituConnectResponse) => {
        if (response.success && response.situConnectId) {
          this.situConnectId = response.situConnectId;
          this.startPollingRenderStatus(response.situConnectId, this.renderType!);
        } else {
          this.showMessage("Sorry! There was an issue at our end retrieving your renders. Please try again.");
        }
      })
      .catch((x: any) => {
        this.showMessage("Sorry! There was an issue at our end retrieving your renders. Please try again.");
      });
  }
  upgrade(situConnectType: string) {
    if (!situConnectType || !this.onSelectedCompanyChange) {
      this.showMessage("Conenct type is required");
      return;
    }
    this.loading = true;
    this.situConnectId = undefined;
    this.stopPollingRenderStatus();

    let request = new CopySituConnectRequest();
    request.companyId = this.selectedCompany?.id;
    request.connectProductTypeCode = situConnectType;
    request.situConnectId = this.selectedSituConnect?.artifactId;

    this.renderServiceClient.copySituConnect(request)
      .then((response: CopySituConnectResponse) => {
        if (response.success && response.situConnectId) {
          this.situConnectId = response.situConnectId;
          this.startPollingRenderStatus(response.situConnectId, situConnectType);
        } else {
          if (response.errorMessages) {
            this.showMessage(response?.errorMessages[0] ?? "Sorry! There was an issue at our end retrieving your renders. Please try again.");
          } else {
            this.showMessage("Sorry! There was an issue at our end retrieving your renders. Please try again.");
          }
        }
      })
      .catch((x: any) => {
        this.showMessage("Sorry! There was an issue at our end retrieving your renders. Please try again.");
      });
  }

  share(forInternal: boolean = false) {
    if (this.situConnectId) {
      if (forInternal) {
        this.clipboard.copy(`${this.configService.config.situPortalBaseUrl}/situ-view?id=${this.situConnectId}`);
        this.showMessage("Copied url to clipboard!");
      } else {
        let productName = "connect";
        let url = this.configService.config.situLinkBaseUrl ?
          `https://${productName}.${this.configService.config.situLinkBaseUrl}/${this.situConnectId}` :
          `${this.configService.config.situPortalBaseUrl}/${this.situConnectId}`;
        this.clipboard.copy(url);
        this.showMessage("Copied situLINK to clipboard!");
      }
    };
  };

  /* polling */
  pollingInterval: NodeJS.Timeout | null = null;
  pollingFails = 0;

  private startPollingRenderStatus(situConnectId: string, renderType: string) {
    if (!this.pollingInterval) {
      this.pollingFails = 0;

      this.loadById(situConnectId, false, renderType); // start immediately in case it's a cached render
      this.pollingInterval = setInterval(() => { this.loadById(situConnectId, true, renderType); }, 10000);
    }
  }

  private stopPollingRenderStatus() {
    if (!!this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }
  /* polling */

  loadById(situConnectId: string, isPolling: boolean = false, type: string | undefined) {
    this.loading = true;

    if (!isPolling) {
      this.paramId = undefined;
      this.situConnectId = undefined;
    }

    let isPano = true;
    if (type) {
      isPano = !(type === 'facade' || type === 'hdfacade');
    }

    let request = new GetSituConnectRequest();
    request.situConnectId = situConnectId;
    this.renderServiceClient.getSituConnect(request)
      .then((response: GetSituConnectResponse) => {
        if (response?.success && response?.queuedSituConnect?.status !== undefined) {
          switch(response.queuedSituConnect.status) {
            case HomeStatus.Complete:
              this.situConnectId = response.queuedSituConnect.artifactId;
              let url = this.generateShareUrl(false);
              if (url) {
                this.quickViewReportedSituLinkParams = this.getQueryParamsFromUrl(url);
                this.quickViewProductName = isPano ? ProductName.Panorama : ProductName.Facade;
              } 
              this.loading = false;
              if (isPolling) {
                this.loadRecipeEventSummaries();
              }
              this.stopPollingRenderStatus();
              break;
          }
        } else {
          this.showMessage("Sorry! There was an issue at our end retrieving your render. Please try again.");
        }
      })
      .catch((err: any) => {
        this.showMessage("Sorry! There was an issue at our end retrieving your render. Please try again.");
      });
  }
  selectedSituConnect: SituConnectSummary | undefined;
  loadSelectedSituConnect(situConnect: SituConnectSummary) {
    if (situConnect) {
      this.baseIngredients = situConnect.baseIngredients;
      this.selectedSituConnect = situConnect;
      this.loadById(situConnect.artifactId!, false, situConnect.connectType);
    }
  }
  baseIngredients: BaseIngredient[] | undefined;

  get customerName(): string {
    return this.firstFormGroup!.get('customerName')?.value || '';
  }
  get customerReference(): string {
    return this.firstFormGroup!.get('customerReference')?.value || '';
  }
  getConnectType(callerReferenceKey: string) {
    return callerReferenceKey?.split("\\")?.reverse()[0];
  }
  getConnectThemes(callerReferenceKey: string) {
    return callerReferenceKey?.split("\\")?.reverse()[1].split("~").reverse();
  }
  getIconType(artifactKey: string): string {
    const connectType = this.getConnectType(artifactKey);
    switch (connectType) {
      case 'draftsitulinkpanoramas':
      case 'situlinkpanoramas':
      case 'hdsitulinkpanoramas':
        return 'panorama'; // the icon for panorama
      case 'facade':
      case 'hdfacade':
        return 'photo'; // the icon for photo
      default:
        return 'panorama'; // fallback icon
    }
  }
  getConnectTypeTooltip(artifactKey: string): string {
    const connectType = this.getConnectType(artifactKey);
    switch (connectType) {
      case 'draftsitulinkpanoramas':
        return 'situVIEW';
      case 'situlinkpanoramas':
        return 'situVIEW HD';
      case 'hdsitulinkpanoramas':
        return 'situVIEW HD+';
      case 'facade':
        return 'situFACADE';
      case 'hdfacade':
        return 'situFACADE HD';
      default:
        return 'Unknown Type';
    }
  }
  getIconColor(artifactKey: string): string {
    const connectType = this.getConnectType(artifactKey);
    switch (connectType) {
      case 'draftsitulinkpanoramas':
        return 'rgba(106, 76, 147, 1)'; // purple
      case 'situlinkpanoramas':
        return 'rgba(25, 130, 196, 1)'; // blue
      case 'hdsitulinkpanoramas':
        return 'rgba(20, 193, 245, 1)'; // light blue
      case 'facade':
      case 'hdfacade':
        return 'rgba(255, 89, 94, 1)'; // red
      default:
        return 'rgba(0, 0, 0, 1)'; // default black
    }
  }
  isViewable(artifactKey: string): boolean {
    const connectType = this.getConnectType(artifactKey);
    switch (connectType) {
      case 'draftsitulinkpanoramas':
      case 'situlinkpanoramas':
      case 'hdsitulinkpanoramas':
      case 'facade':
      case 'hdfacade':
        return true;
      default:
        return false;
    }
  }

  notifyMe: boolean = false;
  quickViewProductName: ProductName | undefined;
  quickViewReportedSituLinkParams: { [key: string]: string } | undefined;
  private getQueryParamsFromUrl(url: string): { [key: string]: string } {
    try {
      const parsedUrl = new URL(url);
      const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);
      const guid = pathSegments[pathSegments.length - 1];
      const searchParams = new URLSearchParams(parsedUrl.search);
      const queryParams: { [key: string]: string } = {};
      searchParams.forEach((value, key) => {
        queryParams[key] = value;
      });
      queryParams['heightoverride'] = "500px";
      queryParams['quickview'] = "true";
      queryParams['id'] = guid;
      return queryParams;
    } catch (error) {
      return {};
    }
  }
  private generateShareUrl(forInternal: boolean = false) {
    if (this.situConnectId) {
      if (forInternal) {
        return `${this.configService.config.situPortalBaseUrl}/situ-connect?id=${this.situConnectId}`;
      } else {
        return this.configService.config.situLinkBaseUrl ?
          `https://${SituConnectComponent.readonlyType}.${this.configService.config.situLinkBaseUrl}/${this.situConnectId}` :
          `${this.configService.config.situPortalBaseUrl}/${this.situConnectId}`;
      }
    };
  }
  private createConnectConfig(customerName: string, customerReference: string, themes: string[]): any {
    return {
        "$type": "SituSystems.RenderService.Contracts.Models.ConnectConfig, SituSystems.RenderService.Contracts",
        "CustomerName": customerName,
        "CustomerReference": customerReference,
        "Themes": {
            "$type": "System.Collections.Generic.List`1[[System.String, System.Private.CoreLib]], System.Private.CoreLib",
            "$values": themes
        },
        "Materials": {
            "$type": "System.Collections.Generic.List`1[[SituSystems.RenderService.Contracts.Models.MaterialThemeEntry, SituSystems.RenderService.Contracts]], System.Private.CoreLib",
            "$values": []
        },
        "Meshes": {
            "$type": "System.Collections.Generic.List`1[[SituSystems.RenderService.Contracts.Models.MeshThemeEntry, SituSystems.RenderService.Contracts]], System.Private.CoreLib",
            "$values": []
        }
    };
  }
  getFacadeImageUrl(situConnectId: string) {
    return `${this.configService.config.situViewWebApiBaseUrl}/home/GetImage?artifactId=${situConnectId}&fileName=facade.jpg`;
  }

  recipeEventSummaries: SituConnectSummary[] | undefined;
  loadRecipeEventSummaries() {
    this.loading = true;
    let request = new ListSituConnectSummariesRequest();
    request.companyId = this.selectedCompany?.id;
    request.pageNumber = 1;
    request.pageSize = 10;
    //request.productName = ProductName.Connect;
    request.recipeStatus = RecipeStatus.Complete;

    this.situAnalyticsClient.listSituConnectSummaries(request)
      .then((response: ListSituConnectSummariesResponse) => {
        if (response.success && response?.items) {
          this.recipeEventSummaries = response.items;
        }
      })
      .catch((x: any) => {
        this.showMessage("Sorry! There was an issue at our end retrieving your renders. Please try again.");
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }
}
